import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useApplyTourOfferDepositToBrand } from '~/hooks/apis/tours/useTourOffers';

interface Props {
  tourOfferId: string;
  brandName: string | null;
  /** The saved deposit is what's applied, so unsaved deposit edits block it. */
  hasUnsavedDeposit: boolean;
}

/** Puts this tour's saved deposit on every tour of its brand. */
export default function NativeTourApplyDepositToBrand({ tourOfferId, brandName, hasUnsavedDeposit }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const apply = useApplyTourOfferDepositToBrand(tourOfferId);
  const [isConfirming, setIsConfirming] = useState(false);
  const brand = brandName ?? 'this brand';

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
      <Button variant="text" disabled={hasUnsavedDeposit || apply.isPending} onClick={() => setIsConfirming(true)}>
        Apply to every {brand} tour
      </Button>
      <Typography variant="body2" color="text.secondary">
        {hasUnsavedDeposit ? 'Save the deposit first.' : 'Uses the saved deposit type and amount.'}
      </Typography>
      <Dialog open={isConfirming} onClose={() => setIsConfirming(false)}>
        <DialogTitle>Apply this deposit to every {brand} tour?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Every {brand} tour gets this tour's deposit type and amount, replacing their own. Their due days and payment
            schedules stay as they are.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsConfirming(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={apply.isPending}
            onClick={async () => {
              try {
                await apply.mutateAsync();
                enqueueSnackbar(`Deposit applied to every ${brand} tour`, { variant: 'success' });
                setIsConfirming(false);
              } catch (error) {
                enqueueSnackbar(`Couldn't apply the deposit: ${error.message}`, { variant: 'error' });
              }
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
